import { useState, useEffect } from "react"
import { 
  BookOpen, Image as ImageIcon, DollarSign, Tag, 
  Award, Users, Clock, FileText, Save, X, Plus, Trash2, Settings, Loader2, Languages, Globe
} from "lucide-react"
import { Button } from "../ui/button"
import ImageUpload from "../ImageUpload"
import { useCategories } from "../../hooks/useCategories"
import TranslationManager from "./TranslationManager"

const SUPPORTED_LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
]

export default function CourseEditForm({
  course,
  onSave,
  onCancel,
  isCreatingNew = false,
  onShowTranslations
}) {
  const [selectedLanguage, setSelectedLanguage] = useState(null) // null (base/fr) | 'en'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    price: '',
    originalPrice: '',
    currency: 'XOF',
    level: 'BEGINNER',
    language: 'Français',
    status: 'DRAFT',
    isFree: false,
    isFeatured: false,
    isPublished: false,
    maxStudents: 100,
    certificate: false,
    certificateTemplate: '',
    accessDurationDays: 90,
    duration: 0,
    requirements: [],
    objectives: [],
    categoryId: '',
    tagIds: [],
    thumbnail: null,
    thumbnailFile: null
  })

  const [newRequirement, setNewRequirement] = useState('')
  const [newObjective, setNewObjective] = useState('')
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  const { categories } = useCategories()

  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        description: course.description || '',
        shortDescription: course.shortDescription || '',
        price: course.price || '',
        originalPrice: course.originalPrice || '',
        currency: course.currency || 'XOF',
        level: course.level || 'BEGINNER',
        language: course.language || 'Français',
        status: course.status || 'DRAFT',
        isFree: course.isFree || false,
        isFeatured: course.isFeatured || false,
        isPublished: course.isPublished || false,
        maxStudents: course.maxStudents || 100,
        certificate: course.certificate || false,
        certificateTemplate: course.certificateTemplate || '',
        accessDurationDays: course.accessDurationDays || 90,
        duration: course.duration || 0,
        requirements: course.requirements || [],
        objectives: course.objectives || [],
        categoryId: course.categoryId || '',
        tagIds: course.tags?.map(tag => tag.id) || [],
        thumbnail: course.thumbnail || null,
        thumbnailFile: null
      })
    }
  }, [course])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleAddItem = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }))
      setter('')
    }
  }

  const handleRemoveItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est obligatoire'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    try {
      await onSave(formData)
    } catch (error) {
      console.error('Error saving course info:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isCreatingNew ? 'Nouveau Cours' : 'Modifier le cours'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isCreatingNew ? 'Définissez les informations de base de votre cours' : 'Modifiez les informations de base du cours'}
                </p>
              </div>
              {/* Language Switch Tabs */}
              {!isCreatingNew && course?.id && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
                  <button
                    onClick={() => {
                      setSelectedLanguage(null) // Base = Français
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      selectedLanguage === null
                        ? 'bg-[#141835] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>🇫🇷</span>
                    <span>Français</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedLanguage('en')
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      selectedLanguage === 'en'
                        ? 'bg-[#141835] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>🇬🇧</span>
                    <span>English</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(formData.thumbnail || course?.thumbnail) && (
              <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 shadow-md">
                <img
                  src={formData.thumbnail || course?.thumbnail}
                  alt={formData.title || course?.title || 'Cours'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {selectedLanguage === 'en' && course?.id ? (
          <TranslationManager
            type="course"
            entityId={course.id}
            entityData={course}
            initialLanguage="en"
            hideHeader={true}
            onClose={() => {
              setSelectedLanguage(null)
            }}
            onTranslationSaved={() => {
              // Optionnel: rafraîchir les données du cours
            }}
          />
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-8">
                <Languages className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Modifier les informations texte du cours
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Les informations texte (titre, description, prérequis, objectifs) sont gérées via les traductions.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Utilisez l'onglet "English" ci-dessus ou la section "Traductions" dans l'inspecteur pour modifier ces informations.
                </p>
                <div className="flex flex-col gap-2 text-sm text-gray-600">
                  <p>• Pour modifier l'image : Utilisez le bouton "Modifier l'image" dans l'inspecteur</p>
                  <p>• Pour modifier la configuration : Utilisez le bouton "Configuration" dans l'inspecteur</p>
                  <p>• Pour modifier les textes : Utilisez la section "Traductions" dans l'inspecteur</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer - Only show for base form (Français) */}
      {selectedLanguage !== 'en' && (
        <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
            >
              Annuler
            </Button>
          )}
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder les informations
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

